// [VV11-TEARDOWN] lp-no-discount-framing A/B test (DEV-10019).
// vv11 — remove all "60% off" framing from the baerskin-hoodie-4 LP + checkout
// for the test arm. Variant rule + copy stripper (single source of truth).
//
// Client-safe: this module must NOT import `next/server` (unlike
// `../middleware/noDiscountFraming.ts`), so it can be pulled into client
// bundles — the LP header/sticky CTA, the Highlight block and the LP checkout
// (`@local/offer`, `@local/ui`) all read the assignment through here.
//
// ─── HOW THIS TEST IS ASSIGNED ──────────────────────────────────────────────
// Standard house pattern, same as vv5 / vv6 / vv10: deterministic FNV-1a
// bucketing of IP + User-Agent into a 60 / 40 control / test split, scoped to
// the baerskin-hoodie-4 LP. A visitor hits a URL and the split happens.
//
// An earlier revision gated assignment on the inbound `d.HOODIE-NO-60-OFF`
// campaign instead, so the ad and the page could not disagree about the
// discount. That was dropped once the ads themselves stopped mentioning 60%
// off — which removed the coherence constraint entirely — and because Rafael
// pointed out the fatal flaw: two campaigns reach two different audiences, so
// it could never have been a valid A/B test no matter how the data was
// segmented afterwards. Decision in the DEV-10019 Slack thread
// (2026-08-06): "assume que é uma campanha só, setup padrão, faz o básico".
//
// ⚠️ DEFERRED, NOT RESOLVED: if the no-discount creative ever runs as its own
// campaign against a separate audience, that confound returns and this split
// stops measuring what it claims to. Rafael: "depois a gente vê os req de
// campanha." Revisit before any multi-campaign rollout.
//
// ─── TEARDOWN CHECKLIST (grep `VV11-TEARDOWN` across the repo) ──────────────
// DELETE these files (entirely test-only):
//   packages/tracking/src/experiments/noDiscountFraming.ts        (this file)
//   packages/tracking/src/middleware/noDiscountFraming.ts
//   packages/tracking/src/middleware/getVv11.ts
//   packages/tracking/src/__tests__/noDiscountFramingMiddleware.test.ts
//   packages/tracking/src/__tests__/noDiscountFramingExperiment.test.ts
//   packages/ui/src/components/Blocks/Highlight/Vv11ExposureBeacon.tsx
//
// REVERT the marked `[VV11-TEARDOWN]` blocks in:
//   packages/tracking/src/middleware/index.ts      (option, resolver, header, cookie)
//   packages/tracking/src/middleware/url/const.ts  (drop the "vv11" entry outright — #4026 convention)
//   packages/tracking/src/trackActions.tsx         (split_test membership)
//   apps/store/src/proxy.ts                        (drop `vv11: true`)
//   packages/offer/src/modules/lp/LpHomeHeader.tsx        (topbar + CTA strip)
//   packages/offer/src/modules/lp/LpHomeStickyCta.tsx     (CTA strip)
//   packages/offer/src/builder.tsx                        (server read + Highlight prop + beacon)
//   packages/offer/src/modules/lp/Checkout/CheckoutOfferBuilder.tsx (headline, marquee, compare prices)
//   packages/ui/src/components/Blocks/Highlight/index.tsx (hideDiscountFraming prop)
//   packages/ui/src/components/Blocks/Highlight/HighlightPrice.tsx (hide compare price + Save %)
//   packages/offer/src/modules/common/StaticPages/CookiePolicy/en.tsx (vv11 cookie row)
//   packages/offer/src/modules/common/StaticPages/CookiePolicy/de.tsx (vv11 cookie row, DE)
//   apps/store/src/tests/policy-pages.spec.ts      (vv11 cookie-policy assertion)
//   packages/devtools/src/panel/components/ABTestingTab.tsx (vv11 EXPERIMENTS entry)
//
// EXTERNAL: archive the `lp-no-discount-framing` PostHog feature flag. If the
// test arm wins, the discount framing is removed permanently via the CMS
// (channel header topbar/callToAction, the promo image, and the listing
// compareAtPrice) — NOT by leaving this cookie gate in place.
// ───────────────────────────────────────────────────────────────────────────

/** Cookie / query-override slot name for the vv11 experiment. */
export const VV11_COOKIE = "vv11"

/**
 * PostHog feature-flag key for this experiment (single source of truth).
 * The `control` / `test` cookie buckets map 1:1 to this flag's variant keys.
 * Deliberately human-readable and distinct from the cookie shorthand, matching
 * the house convention (vv6 → `store-apm`, vv10 → `lp-sku-pricing-modifiers`).
 */
export const VV11_FEATURE_FLAG = "lp-no-discount-framing"

/**
 * The single landing-page channel this experiment runs on. Its ainchor
 * sub-pages (`/lp/baerskin-hoodie-4/{ainchor}`) and its checkout are in scope
 * too — the brief's "and it's Variations". Missing the sub-pages is how vv5
 * polluted its own results (DEV-9882 / PR #3975).
 */
export const NO_DISCOUNT_LP_CHANNEL = "baerskin-hoodie-4"

/**
 * Valid variant values for the vv11 experiment.
 *   - control → today's LP + checkout, all "60% off" framing shown
 *   - test    → identical page with every "-60%" mention removed; the price
 *               actually charged is untouched (this tests the framing, not the
 *               price)
 */
export const VV11_VARIANTS = ["control", "test"] as const

export type Vv11Variant = (typeof VV11_VARIANTS)[number]

/** Type guard for anything read back from the cookie / query override. */
export const isVv11Variant = (value: unknown): value is Vv11Variant =>
  VV11_VARIANTS.includes(value as Vv11Variant)

/**
 * Whether the discount framing should be hidden. Only the `test` arm hides it;
 * a missing / invalid / control value keeps today's messaging (safe default —
 * matches current production exactly).
 */
export const hideDiscountFraming = (vv11?: string | null): boolean =>
  vv11 === "test"

// ─── Path scope ────────────────────────────────────────────────────────────

/**
 * Whether a pathname is the baerskin-hoodie-4 LP or anything beneath it
 * (`/{market}/lp/baerskin-hoodie-4`, its ainchor sub-pages, its checkout) — but
 * NOT a different LP that merely starts with the slug (e.g.
 * `…/baerskin-hoodie-4-sale`), because the slug must terminate at a `/` or the
 * end of the path. Mirrors `parseNoDiscountPath` in the middleware so a
 * persisted `vv11` cookie can't strip discount copy off a look-alike route.
 */
const NO_DISCOUNT_LP_PATH_RE = new RegExp(
  `/lp/${NO_DISCOUNT_LP_CHANNEL}(?:/|$)`
)

export const isNoDiscountLpPath = (pathname?: string | null): boolean =>
  !!pathname && NO_DISCOUNT_LP_PATH_RE.test(pathname)

/**
 * Whether the discount framing should be hidden for a client-side surface.
 *
 * True only on the `test` arm AND when we're actually on the baerskin-hoodie-4
 * LP — detected by the cart/checkout channel slug OR the URL pathname, so a
 * component shared with other landing pages (the checkout cart summary, the
 * style selector) can't have its compare prices stripped by a `vv11` cookie
 * that a visitor picked up elsewhere. Mirrors `isFreeShippingTestActive`.
 *
 * Pure — callers pass the pathname and cookie value, so it stays edge-safe and
 * unit-testable.
 */
export const isNoDiscountFramingActive = ({
  channel,
  pathname,
  vv11,
}: {
  channel?: string
  pathname?: string | null
  vv11?: string
}): boolean =>
  hideDiscountFraming(vv11) &&
  (channel === NO_DISCOUNT_LP_CHANNEL || isNoDiscountLpPath(pathname))

// ─── Arm carrier fallback (sessionStorage) ─────────────────────────────────

/** sessionStorage key mirroring the `vv11` cookie. */
export const VV11_STORAGE_KEY = "vv11"

/**
 * Why this exists.
 *
 * The LP resolves its arm from the `x-vv11` REQUEST HEADER, which middleware
 * sets on every request. The LP checkout is intentionally static, so it can only
 * read the COOKIE in the browser. If the cookie does not survive — blocked, or
 * purged by the consent manager — the LP strips the discount framing while the
 * checkout still shows "60% OFF" and a struck-through compare price, and the
 * shopper can complete that purchase (the cart persists to localStorage, not
 * cookies). Reported by rafzan on PR #4036.
 *
 * The LP therefore also writes the arm to sessionStorage, and the checkout reads
 * `cookie ?? sessionStorage`. Both surfaces then agree even with no cookie at
 * all.
 *
 * sessionStorage, NOT localStorage — deliberately. It is scoped to the tab
 * session, which is exactly the "LP → checkout in this journey" case being
 * fixed, and it cannot resurrect a stale arm: a direct-to-checkout landing in a
 * fresh tab finds nothing here and falls through to whatever middleware just
 * assigned, so the two can never disagree. A long-lived localStorage copy could
 * hand the checkout a weeks-old arm that contradicts a fresh assignment.
 *
 * Every access is wrapped — storage access throws outright in some privacy
 * modes, and this is a fallback, so it must never be the thing that breaks.
 */
export const writeVv11Fallback = (variant: Vv11Variant): void => {
  if (typeof window === "undefined") return
  try {
    window.sessionStorage.setItem(VV11_STORAGE_KEY, variant)
  } catch {
    // storage unavailable — the cookie remains the primary carrier
  }
}

/** Read the arm written by the LP. Returns undefined when absent/invalid. */
export const readVv11Fallback = (): Vv11Variant | undefined => {
  if (typeof window === "undefined") return undefined
  try {
    const value = window.sessionStorage.getItem(VV11_STORAGE_KEY)
    return isVv11Variant(value) ? value : undefined
  } catch {
    return undefined
  }
}

// ─── Copy stripper ─────────────────────────────────────────────────────────

/**
 * Percentage-discount tokens, longest-first so `-60% OFF` is consumed before a
 * shorter pattern can bite into it.
 *
 * ⚠️ EVERY unsigned percentage MUST carry discount context (`OFF`, `SAVE`,
 * `discount`). An unsigned bare `nn%` is NEVER stripped, because product and
 * badge copy on these very pages contains non-discount percentages — the LP
 * renders a "100% QUALITY" trust badge, and listing copy contains things like
 * "100% cotton". An earlier revision matched bare `nn%` and turned
 * "100% QUALITY" into "QUALITY". A bare percentage is only removed when it is
 * explicitly signed (`-60%`, `–60%`), which unambiguously reads as a discount.
 *
 * `60-DAY RETURNS` is safe for a second reason too: every pattern requires a
 * literal `%`, and `60-DAY` has none.
 *
 * Quantity-tier copy ("EXTRA 5% OFF", "EXTRA 10% OFF") IS matched, which is why
 * the stripper is never applied to tier labels — see
 * `getQuantityDiscountMessage` / `quantityOptions` in `CheckoutOfferBuilder`,
 * which stay untouched per the DEV-10019 screenshots (the tiers are not the 60%
 * framing under test).
 */
const DISCOUNT_TOKEN_PATTERNS: RegExp[] = [
  // "SAVE 60%", "Save 60%", "SAVE 60% OFF", "Save 60% off today",
  // "SAVE 60% discount".
  // The trailing noun is part of THIS pattern, not left to the `nn% OFF` one
  // below: this pattern runs first and would otherwise consume only "SAVE 60%",
  // orphaning the word — which `collapseGaps` cannot absorb because it is not a
  // connective, so "SAVE 60% OFF" stripped to "OFF". Both nouns are listed for
  // the same reason: with only `off` here, "SAVE 60% discount" stripped to
  // "discount" (rafzan, PR #4036).
  /\bsave\s*[-–]?\s*\d+(?:\.\d+)?\s*%(?:\s*(?:off|discount)\b)?/gi,
  // "-60% OFF", "60% OFF", "60 % off", and the noun form "a 60% discount!".
  // The noun is consumed too, not preserved by a lookahead: "with a discount!"
  // still frames the offer as discounted, which is exactly what the test arm
  // removes (DEV-10019 screenshot). `collapseGaps` absorbs the orphaned
  // "with a" lead-in, so "…order X with a 60% discount!" → "…order X!".
  /[-–]?\s*\d+(?:\.\d+)?\s*%\s*(?:off|discount)\b/gi,
  // "%%DISCOUNT%% off" / "%%DISCOUNT%%% discount" placeholder forms, and the
  // bare placeholder. The `[-–]?\s*` prefix matches the signed patterns above
  // and below: without it "-%%DISCOUNT%%% OFF" stripped to a bare "-", because
  // the sign is not part of the token and no cleanup rule removes it
  // (rafzan, PR #4036).
  /[-–]?\s*%%DISCOUNT%%\s*%?\s*(?:off|discount)\b/gi,
  /[-–]?\s*%%DISCOUNT%%\s*%?/gi,
  // SIGNED bare percentage only — "-60%", "– 60%". Never unsigned (see above).
  /[-–]\s*\d+(?:\.\d+)?\s*%/g,
]

/**
 * Sentinel standing in for a removed discount token, so the cleanup pass can
 * see exactly where copy went missing and absorb the connective that used to
 * join it to the rest of the sentence. Replacing tokens with a plain space
 * loses that position and leaves orphans like `"GET + FAST & FREE DELIVERY!"`.
 *
 * U+0000 can't appear in CMS copy, so it is unambiguous.
 */
const GAP = "\u0000"

/**
 * Connectives and arrow separators that become meaningless once one of their
 * operands is removed. `&` is deliberately absent: it joins words inside
 * surviving copy ("FAST & FREE DELIVERY") far more often than it joins a
 * discount token, and an over-eager rule mangles the remainder.
 */
const ORPHAN_CONNECTIVE = String.raw`[+,|·•]`
const ARROW = String.raw`[❱▶►>›»]`

/**
 * Same connectives plus `&`, for the boundary-only tidy at the end of
 * `collapseGaps`.
 *
 * `&` is safe HERE and only here. Mid-string it joins surviving copy
 * ("FAST & FREE DELIVERY"), which is why `ORPHAN_CONNECTIVE` above excludes it —
 * but at `^` or `$` there is nothing left on one side, so the `&` is always
 * broken regardless of what it once joined. Without this,
 * "60% OFF & FAST FREE DELIVERY" stripped to "& FAST FREE DELIVERY" and
 * "FAST FREE DELIVERY & 60% OFF" to "FAST FREE DELIVERY &"
 * (rafzan, PR #4036).
 */
const ORPHAN_CONNECTIVE_AT_EDGE = String.raw`[+,|·•&]`

/**
 * Lead-ins that only make sense in front of the amount they introduce.
 * `"UP TO 60% OFF + FREE DELIVERY"` would otherwise strip to
 * `"UP TO FREE DELIVERY"`, and `"…order X with a 60% discount!"` to
 * `"…order X with a!"` — both broken copy, and both plausible per-market CMS
 * phrasings, so they are worth absorbing rather than leaving to copy review.
 * Deliberately narrow: only phrases that are meaningless without a following
 * amount. `GET` / `SAVE` / `EXTRA` are NOT here — "GET FAST & FREE DELIVERY!"
 * reads fine, and `SAVE nn%` is already consumed whole by its own pattern.
 */
const ORPHAN_QUANTIFIER = String.raw`\b(?:up\s+to|upto|with\s+an?)\b`

/**
 * Collapse the gaps left by removed discount tokens, absorbing any connective
 * or arrow that is now dangling on either side of the hole.
 *
 *   "60% OFF + FAST & FREE USA DELIVERY + 60-DAY RETURNS!"
 *     → "␀ + FAST & FREE USA DELIVERY + 60-DAY RETURNS!"
 *     → "FAST & FREE USA DELIVERY + 60-DAY RETURNS!"
 *
 *   "GET 60% OFF + FAST & FREE DELIVERY!"    → "GET FAST & FREE DELIVERY!"
 *   "AUG 5: -60% OFF ❱ SELECT SIZE NOW"      → "AUG 5: SELECT SIZE NOW"
 *   "SALE – UP TO 60% OFF + FREE DELIVERY"   → "SALE – FREE DELIVERY"
 *   "…order X with a 60% discount!"          → "…order X!"
 */
const collapseGaps = (text: string): string =>
  text
    // an arrow immediately on either side of a gap is now pointing at nothing
    .replace(new RegExp(String.raw`\s*${ARROW}\s*${GAP}`, "g"), GAP)
    .replace(new RegExp(String.raw`${GAP}\s*${ARROW}\s*`, "g"), GAP)
    // a lead-in with nothing left to introduce ("UP TO ␀", "with a ␀")
    .replace(new RegExp(String.raw`${ORPHAN_QUANTIFIER}\s*${GAP}`, "gi"), GAP)
    // absorb the connective that joined the removed token to its neighbour
    .replace(new RegExp(String.raw`\s*${ORPHAN_CONNECTIVE}\s*${GAP}`, "g"), GAP)
    .replace(new RegExp(String.raw`${GAP}\s*${ORPHAN_CONNECTIVE}\s*`, "g"), GAP)
    // the gaps themselves are gone now
    .replace(new RegExp(GAP, "g"), " ")
    .replace(/\s+/g, " ")
    // tidy what the removal exposed: leading connectives, doubled separators,
    // space before punctuation, dangling trailing separators
    .replace(new RegExp(String.raw`^\s*${ORPHAN_CONNECTIVE_AT_EDGE}\s*`), "")
    .replace(new RegExp(String.raw`\s*${ORPHAN_CONNECTIVE_AT_EDGE}\s*$`), "")
    .replace(/\s+([!?.,;:])/g, "$1")
    .replace(/([:：])\s*$/, "$1")
    .trim()

/**
 * Remove every percentage-discount mention from a piece of CMS or fallback
 * copy, leaving the rest of the sentence intact.
 *
 * Used for the LP header topbar, the LP/sticky call-to-action, the checkout
 * marquee and the checkout urgency headline — all of which are CMS-authored
 * per channel and per market, so a regex strip is the only approach that
 * covers "all regions/currencies" without hardcoding eight translations.
 *
 * Returns the input unchanged when it contains no discount token, and returns
 * `undefined`/`null` unchanged so callers can pass optional CMS fields
 * straight through.
 *
 * ⚠️ LIMITATION — the output of a regex strip is not automatically publishable
 * copy. Each removed token absorbs the connective that joined it to its
 * neighbour, which reads correctly when the token sits at the start or end of a
 * list ("60% OFF + FAST DELIVERY" → "FAST DELIVERY") but degrades when one
 * string carries several discount mentions ("60% OFF + FREE SHIPPING + 20% OFF
 * PATCHES" → "FREE SHIPPING PATCHES"). Before launch, the stripped topbar,
 * CTA and urgency headline must be eyeballed in EVERY market — the QA step in
 * DEV-10019 covers this. Where a market's stripped copy reads badly, the fix is
 * for the growth team to author a test-arm string in the CMS, not to bolt more
 * special cases onto this regex.
 */
export const stripDiscountFraming = <T extends string | null | undefined>(
  text: T
): T => {
  if (!text) return text

  let result: string = text
  for (const pattern of DISCOUNT_TOKEN_PATTERNS) {
    result = result.replace(pattern, GAP)
  }

  if (result === text) return text

  return collapseGaps(result) as T
}

/**
 * Order-payload tag for server-side per-arm revenue analysis (survives
 * ad-blockers, unlike client PostHog). Returns
 * `{ experiment_lp_no_discount_framing: <variant> }` only for orders placed on
 * the baerskin-hoodie-4 LP checkout with a valid variant, else `{}` so
 * unrelated orders aren't tagged. Pure (no cookie/window access) so it stays
 * edge-safe — callers pass the pathname + cookie value.
 */
export const noDiscountFramingOrderTag = (
  pathname: string | null | undefined,
  vv11: string | undefined
):
  { experiment_lp_no_discount_framing: Vv11Variant } | Record<string, never> =>
  isNoDiscountLpPath(pathname) && isVv11Variant(vv11)
    ? { experiment_lp_no_discount_framing: vv11 }
    : {}
